import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import "./MessageBox.css";

// components
import EditableMessageBox from "../EditableMessageBox/EditableMessageBox";
import MarkdownContent from "../MarkdownContent/MarkdownContent";
import FeedbackButtons from "../FeedbackButtons/FeedbackButtons";
import AssistantMessageExtras from "../AssistantMessageExtras/AssistantMessageExtras";
import DeepInsightsMessageAddons from "../DeepInsightsMessageAddons/DeepInsightsMessageAddons";
import MessageGraphs from "../MessageGraphs/MessageGraphs";
import ProgressSteps from "../ProgressSteps/ProgressSteps";

// assets
import copyInactive from "../../assets/new-images/copy-icon.svg";
import copyActive from "../../assets/new-images/active-copy-icon.svg";
import editIcon from "../../assets/new-images/edit-icon.svg";
import downloadIcon from "../../assets/new-images/download-icon.svg";
import savePromptIcon from "../../assets/new-images/prompt-save-icon.svg";

// helpers
import { useConstants } from "../../constant/constantProvider";
import { useEventEmitter } from "../../contexts/EventEmmiter";
import { getInitials } from "../../utils/commonHelper";
import { getPlainTextFromHtml, copyRichTextToClipboard } from "../../utils/textUtils";
import { formatShortDate, formatFullDateWithTime } from "../../utils/dateUtils";
import GenericTooltip from "../GenericTooltip/GenericTooltip";

import VoiceOutputPlayer from "../VoiceOutputPlayer/VoiceOutputPlayer";
import useVoiceOutput from "../../hooks/useVoiceOutput";
import useStreamingVOP from "../../hooks/useStreamingVOP";

// ── RotatingText (loading indicator) ─────────────────────────────────────
const RotatingText = ({ words = ["Creating", "Processing", "Analysing"] }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [words.length]);

  return <span>{words[index]}...</span>;
};

// ── MessageBox ────────────────────────────────────────────────────────────
const MessageBox = ({
  pair,
  index,
  editingId,
  editedText,
  setEditedText,
  fullname,
  isTextBoxDisabled,
  showEditPrompt = true,
  handleEditClick,
  handleSave,
  setEditingId,
  botIcon,
  isLoading,
  isLastMessage,
  renderMediaSection,
  useMathJax = true,
  onRegenerate,
  onFeedback,
  models = [],
  feedbackState = null,
  suggestions = [],
  citations = [],
  sourceLinks = [],
  onSuggestionClick,
  generatedBy,
  onCitationClick,
  discussionPoints = [],
  isWebSearch = false,
  showFeedback = [],
  renderContent = null,
  renderExtras = null,
  showTimeStamp = false,
  thinkingEnabled = false,
  charLimit = 5000,
  onSavePrompt = null,
  showProgressSteps = false,
  enableVoiceOutput = false,
  autoPlayVoice = true,
  onVoiceOutputEnd,
  onVoiceOutputStart,
  hideMetadata = false,
  isStreamingVop = false,
  isAzureVop = false,
  streamingVopWsUrl = null,
  azureVopWsUrl = null,
  graphVariant = "default",
  theme = "default",
  expandedVOP = false,
}) => {
  const isVop = isStreamingVop || isAzureVop;
  const vopWsUrl = streamingVopWsUrl || azureVopWsUrl;
  const messageContentRef = useRef(null);
  const { emit, subscribe } = useEventEmitter();
  const { ANALYSING_YOUR_DOCUMENT, GENERATING_YOUR_IMAGE, SAVE, CANCEL, COPY_SUCCESS, COPY, EDIT, chat } = useConstants();
  const isEditing = editingId === index;
  const isAssistant = pair.role === "assistant" || pair.role === "bot";
  // MessageBox is shared; only DeepInsights replaces its bot icon while generating.
  const isDeepInsightsPreparing =
    theme === "deep-insights" &&
    isAssistant &&
    (pair.isPlaceholder || (isLoading && isLastMessage));
  const [isCopied, setIsCopied] = useState(false);
  const [hasVoiceStarted, setHasVoiceStarted] = useState(false);

  const defaultVoice = useVoiceOutput({
    content: pair.content,
    isPlaceholder: pair.isPlaceholder || (isLoading && isLastMessage),
    enableVoiceOutput,
    autoPlayVoice,
    isAssistant,
    onVoiceOutputEnd,
    onVoiceOutputStart,
    isStreamingVop: isVop,
    isAzureVop: isVop,
  });

  const streamingVoice = useStreamingVOP({
    content: pair.content,
    isPlaceholder: pair.isPlaceholder || (isLoading && isLastMessage),
    enableVoiceOutput,
    autoPlayVoice,
    isAssistant,
    onVoiceOutputEnd,
    onVoiceOutputStart,
    isStreamingVop: isVop,
    wsUrl: vopWsUrl,
  });

  const { isVoicePlaying, isVoiceUnavailable, handleVoiceToggle } = isVop ? streamingVoice : defaultVoice;

  useEffect(() => {
    if (isVoicePlaying) {
      setHasVoiceStarted(true);
    }
  }, [isVoicePlaying]);

  const initials = getInitials(fullname);

  const [isVoiceInputActive, setIsVoiceInputActive] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribe("voiceInputStateChanged", (isActive) => {
      setIsVoiceInputActive(isActive);
    });
    return () => unsubscribe();
  }, [subscribe]);

  const handleEditClickWrapper = () => {
    if (handleEditClick) {
      if (handleEditClick.length === 3) {
        handleEditClick(index, pair.role, pair.id);
      } else {
        handleEditClick(index, pair);
      }
    }
  };

  const handleCopy = async () => {
    if (pair?.content) {
      const plainText = !isAssistant ? getPlainTextFromHtml(pair.content) : pair.content;
      const htmlContent = !isAssistant ? pair.content : "";

      const success = await copyRichTextToClipboard(plainText, htmlContent);

      if (success) {
        setIsCopied(true);
        toast.info(COPY_SUCCESS || "Copied to clipboard!", { autoClose: 1000 });
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      } else {
        toast.error("Failed to copy to clipboard");
      }
    }
  };

  return (
    <div
      className={`message-container ${isAssistant ? "bot-message-wrapper" : "user-message-wrapper"
        }`}
    >
      {isAssistant && (
        <div className="assistant-row">
          <div className={`bot-icon-container ${isDeepInsightsPreparing ? "is-deep-insights-preparing" : ""}`}>
            {isDeepInsightsPreparing ? (
              <span className="deep-insights-thinking-orb" aria-hidden="true" />
            ) : (
              <img src={botIcon} alt="bot" />
            )}
          </div>

          <div className="assistant-content-col" data-pair-id={pair.id}>

            {/* ── Voice Output Player card ───────────────────── */}
            {enableVoiceOutput && (
              <VoiceOutputPlayer
                content={pair.content || ""}
                isPlaying={isVoicePlaying}
                isUnavailable={isVoiceUnavailable}
                onToggle={handleVoiceToggle}
                isStreaming={(!!pair.isPlaceholder || pair.content === "Loading..." || (theme !== "deep-insights" && !pair.content)) && !hasVoiceStarted}
                spokenDuration={pair.spokenDuration || null}
                isVoiceInputActive={isVoiceInputActive}
                theme={theme}
                expandedVOP={expandedVOP}
                steps={expandedVOP && isVoicePlaying && pair.steps?.length ? pair.steps : undefined}
              />
            )}

            <div className={`message-content ${pair.content === "Loading..." ? "loader-message" : ""}`}>
              {isAssistant && pair.isFileUploading && pair.content === "Loading..." ? (
                <div className="loader-inner-section">
                  <div className="loading-pulse"></div>
                  <span className="loading-text">
                    <RotatingText words={[ANALYSING_YOUR_DOCUMENT, "Extracting Information", "Processing Content", "Almost ready"]} />
                  </span>
                </div>
              ) : (
                <div className="math-jsx" ref={messageContentRef}>
                  {showProgressSteps && pair.isPlaceholder && pair.steps?.length > 0 && (
                    <ProgressSteps steps={pair.steps} isActive={pair.isPlaceholder} />
                  )}
                  {renderContent ? (
                    renderContent(pair, index)
                  ) : (
                    (!showProgressSteps || !pair.isPlaceholder || (pair.content && pair.content !== "Loading...")) && (
                      <MarkdownContent
                        content={pair.content === "Loading..." ? "" : pair.content}
                        isStreaming={isLoading && isLastMessage}
                        useMathJax={useMathJax}
                        enableSectionCopy={true}
                      />
                    )
                  )}
                  {pair.content === "Loading..." && !renderContent && pair.session_type !== "IMAGE_GENERATION" && !showProgressSteps && (
                    <div className="loader-inner-section">
                      <div className="loading-pulse"></div>
                      <span className="loading-text">
                        <RotatingText />
                      </span>
                    </div>
                  )}
                </div>
              )}

              {(pair?.image_data || (pair?.session_type === "IMAGE_GENERATION" && pair.content === "Loading...")) && (
                <div className={`generated-image ${!(pair?.image_data?.isLoading || pair.content === "Loading...") ? "has-image" : ""}`}>
                  {(pair?.image_data?.isLoading || pair.content === "Loading...") ? (
                    <div className="loader-inner-section">
                      <div className="loading-pulse"></div>
                      <span className="loading-text">
                        <RotatingText words={[GENERATING_YOUR_IMAGE, "Rendering Pixels", "Applying Styles", "Optimizing Image"]} />
                      </span>
                    </div>
                  ) : (
                    <>
                      <img
                        src={`data:image/png;base64,${pair.image_data.base64_image}`}
                        alt="generated"
                      />
                      <button
                        className="image-download-btn"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = `data:image/png;base64,${pair.image_data.base64_image}`;
                          link.download = `generated-image-${Date.now()}.png`;
                          link.click();
                        }}
                        title="Download Image"
                      >
                        <img src={downloadIcon} alt="download" />
                      </button>
                    </>
                  )}
                </div>
              )}
              <MessageGraphs
                graph={pair?.graph || pair?.graphs}
                variant={graphVariant}
              />
            </div>

            {pair.role === "assistant" && pair.content !== "Loading..." && (
              <AssistantMessageExtras
                suggestions={suggestions}
                citations={citations}
                sourceLinks={sourceLinks}
                onSuggestionClick={onSuggestionClick}
                onCitationClick={onCitationClick}
                pairId={pair.id}
                generatedBy={generatedBy}
                discussionPoints={discussionPoints}
                isThinking={!!pair.is_thinking}
                isWebSearch={!!pair.is_web_search || isWebSearch}
                isImageGeneration={pair?.session_type === "IMAGE_GENERATION" || pair?.image_data}
                hideMetadata={hideMetadata}
                isLoading={isLoading}
              >
                {renderExtras && renderExtras(pair, index)}
                {!(isLoading && isLastMessage) && (
                  <div className="assistant-actions">
                    {showFeedback && (() => {
                      let option = [];
                      let moreOption = [];

                      if (Array.isArray(showFeedback)) {
                        const KEBAB_TYPES = ["pdf", "docx", "audio"];
                        moreOption = showFeedback.filter(t => KEBAB_TYPES.includes(t));
                        option = showFeedback.filter(t => !KEBAB_TYPES.includes(t));
                      } else if (showFeedback && typeof showFeedback === "object") {
                        option = showFeedback.option || [];
                        moreOption = showFeedback.moreOption || [];
                      }

                      if (option.length === 0 && moreOption.length === 0) return null;

                      return (
                        <FeedbackButtons
                          show={option}
                          moreOption={moreOption}
                          pair={pair}
                          index={index}
                          pairId={pair.id}
                          feedback={feedbackState}
                          isLoading={isLoading}
                          isLastMessage={isLastMessage}
                          onFeedback={onFeedback}
                          onRegenerate={(idx, opt) => onRegenerate?.(idx, opt)}
                          getTextElement={() => messageContentRef.current}
                          models={models}
                          thinkingEnabled={thinkingEnabled}
                          isImageGeneration={pair?.session_type === "IMAGE_GENERATION" || pair?.image_data}
                        />
                      );
                    })()}
                  </div>
                )}
              </AssistantMessageExtras>
            )}
          </div>
          
            {theme === "deep-insights" && ((pair.sources && pair.sources.length > 0) || (pair.follow_up_questions && pair.follow_up_questions.length > 0)) && (
              <DeepInsightsMessageAddons
                sources={pair.sources}
                followUpQuestions={pair.follow_up_questions}
                onFollowUpClick={!isLoading ? onSuggestionClick : undefined}
                disabled={isLoading}
              />
            )}
        </div>
      )}

      {!isAssistant && (
        <div className="user-row user-message-wrapper">
          <div className="user-content-col" data-pair-id={pair.id}>
            {renderMediaSection && renderMediaSection(pair)}

            <div className="user-message-with-actions">

              <div className={isEditing ? "user-message-content-editing" : "user-message-content-section"}>
                {isEditing ? (
                  <EditableMessageBox
                    value={editedText}
                    onChange={setEditedText}
                    className="message-editor"
                    maxLength={charLimit}
                    onSave={() => handleSave(index, pair)}
                    onCancel={() => { setEditingId(null); setEditedText(""); }}
                    saveLabel={SAVE}
                    cancelLabel={CANCEL}
                  />
                ) : (
                  <div className="user-text-content">
                    <MarkdownContent content={pair.content} useMathJax={useMathJax} />
                  </div>
                )}
              </div>
            </div>
            <div className="user-actions-left">
              {showTimeStamp && pair?.timestamp && (
                <GenericTooltip
                  tooltipText={formatFullDateWithTime(pair.timestamp)}
                  tooltipPosition="bottom"
                >
                  <span className="action-timestamp">
                    {formatShortDate(pair.timestamp)}
                  </span>
                </GenericTooltip>
              )}
              <GenericTooltip tooltipText={COPY} tooltipPosition="bottom">
                <button className="action-btn" onClick={handleCopy}>
                  <img src={isCopied ? copyActive : copyInactive} alt="copy" />
                </button>
              </GenericTooltip>
              {showEditPrompt && !isEditing && (
                <GenericTooltip tooltipText={EDIT} tooltipPosition="bottom">
                  <button
                    className={`action-btn ${isTextBoxDisabled ? "disabled" : ""}`}
                    onClick={handleEditClickWrapper}
                    disabled={isTextBoxDisabled}
                  >
                    <img src={editIcon} alt="edit" />
                  </button>
                </GenericTooltip>
              )}
              {onSavePrompt && !isEditing && (
                <GenericTooltip tooltipText={chat?.SAVE_PROMPT} tooltipPosition="bottom">
                  <button
                    id={`save-prompt-btn-${index}`}
                    className="action-btn save-prompt-action-btn"
                    onClick={() => emit("openSavePromptModal", pair.content)}
                  >
                    <img src={savePromptIcon} alt="save prompt" />
                  </button>
                </GenericTooltip>
              )}
            </div>
          </div>
          <div className="profile-icon mobile-profile-icon">
            {initials}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageBox;
